import { FC, ReactNode, Children, isValidElement, ReactElement } from 'react';
import { useReducedMotion } from 'motion/react';
import { ease, duration } from 'styled/tools/motion';
import {
  ButtonRoot,
  LinkRoot,
  Content,
  IconSlot,
  AnimatedIconSlot,
} from './styles';

export type TextButtonSize = 'sm' | 'md';

// Animation variants

const cubicEase = ease as [number, number, number, number];
const transition = { ease: cubicEase, duration };

const hidden = {
  width: 0,
  opacity: 0,
  marginLeft: 0,
  marginRight: 0,
  transition,
};
const visible = { width: 'auto', opacity: 1, transition };

const makeIconVariant = (margin: 'marginLeft' | 'marginRight') => ({
  hidden,
  visible: { ...visible, [margin]: 'var(--spacing-4)', transition },
});

const trailingIconVariants = makeIconVariant('marginLeft');
const leadingIconVariants = makeIconVariant('marginRight');

const animateProps = {
  initial: 'hidden' as const,
  whileHover: 'visible' as const,
  whileFocus: 'visible' as const,
  whileTap: 'visible' as const,
};

// Helpers

const isIcon = (child: ReactNode): child is ReactElement =>
  isValidElement(child) && typeof (child as ReactElement).type !== 'string';

const splitChildren = (children: ReactNode) => {
  const arr = Children.toArray(children);
  let leading: ReactElement | null = null;
  let trailing: ReactElement | null = null;
  const text: ReactNode[] = [];

  for (const [i, child] of arr.entries()) {
    if (isIcon(child)) {
      if (i === 0) leading = child;
      else trailing = child;
    } else {
      text.push(child);
    }
  }

  return { leading, trailing, text };
};

export interface TextButtonProps {
  light?: boolean;
  size?:
    | TextButtonSize
    | {
        base: TextButtonSize;
        tablet?: TextButtonSize;
        desktop?: TextButtonSize;
      };
  href?: string;
  newWindow?: boolean;
  onClick?: (event?: any) => void;
  children: ReactNode;
  disabled?: boolean;
  animate?: boolean; // icon is hidden by default, animates in on hover
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-expanded'?: boolean;
  'aria-pressed'?: boolean | 'mixed';
  'aria-controls'?: string;
  'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
}

const TextButton: FC<TextButtonProps> = ({
  light,
  size = { base: 'sm', desktop: 'md' },
  href,
  newWindow,
  disabled,
  onClick,
  children,
  animate,
  leadingIcon,
  trailingIcon,
  className,
  type = 'button',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
  'aria-expanded': ariaExpanded,
  'aria-pressed': ariaPressed,
  'aria-controls': ariaControls,
  'aria-haspopup': ariaHaspopup,
}) => {
  const shouldReduceMotion = useReducedMotion();

  const base = typeof size === 'string' ? size : size.base;
  const tablet = typeof size === 'string' ? undefined : size.tablet;
  const desktop = typeof size === 'string' ? undefined : size.desktop;

  const styleProps = {
    $light: light,
    $sizeBase: base,
    $sizeTablet: tablet,
    $sizeDesktop: desktop,
    $animate: animate,
  };

  const ariaProps = {
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedby,
    'aria-expanded': ariaExpanded,
    'aria-pressed': ariaPressed,
    'aria-controls': ariaControls,
    'aria-haspopup': ariaHaspopup,
  };

  const motionProps = animate && !shouldReduceMotion ? animateProps : {};

  let inner: ReactNode;

  if (animate) {
    const { leading, trailing, text } = splitChildren(children);
    inner = shouldReduceMotion ? (
      <>
        {leading}
        <Content>{text}</Content>
        {trailing}
      </>
    ) : (
      <>
        {leading && (
          <AnimatedIconSlot variants={leadingIconVariants} aria-hidden>
            {leading}
          </AnimatedIconSlot>
        )}
        <Content>{text}</Content>
        {trailing && (
          <AnimatedIconSlot variants={trailingIconVariants} aria-hidden>
            {trailing}
          </AnimatedIconSlot>
        )}
      </>
    );
  } else {
    inner = (
      <>
        {leadingIcon && <IconSlot aria-hidden>{leadingIcon}</IconSlot>}
        <Content>{children}</Content>
        {trailingIcon && <IconSlot aria-hidden>{trailingIcon}</IconSlot>}
      </>
    );
  }

  if (href) {
    return (
      <LinkRoot
        {...styleProps}
        {...motionProps}
        {...ariaProps}
        href={href}
        target={newWindow ? '_blank' : undefined}
        rel={newWindow ? 'noopener noreferrer' : undefined}
        className={className}
        onClick={onClick}
      >
        {inner}
        {newWindow && !ariaLabel && (
          <span className='screenreader-only'>(opens in new tab)</span>
        )}
      </LinkRoot>
    );
  }

  return (
    <ButtonRoot
      {...styleProps}
      {...motionProps}
      {...ariaProps}
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={className}
    >
      {inner}
    </ButtonRoot>
  );
};

export default TextButton;

/**
 * HOW TO IMPLEMENT - TEXTBUTTON
 * ---------------
 *
 * @example Dark (default) — use on light backgrounds
 * <TextButton onClick={handleClick}>Learn more</TextButton>
 *
 * @example Light — use on dark/colored backgrounds
 * <TextButton light>Learn more</TextButton>
 *
 * @example With leading icon
 * <TextButton leadingIcon={<ArrowLeft />}>Go back</TextButton>
 *
 * @example With trailing icon
 * <TextButton trailingIcon={<ArrowRight />}>View all</TextButton>
 *
 * @example Animated icon (reveals on hover) — pass icon as child, position determines leading/trailing
 * <TextButton animate>View all <ArrowRight /></TextButton>
 * <TextButton animate><ArrowLeft /> Go back</TextButton>
 *
 * @example Responsive size
 * <TextButton size={{ base: 'sm', desktop: 'md' }}>Details</TextButton>
 *
 * @example As link
 * <TextButton href="/products">Shop now</TextButton>
 *
 * @example External link
 * <TextButton href="https://seed.com" newWindow>Read study</TextButton>
 *
 * @example Disabled
 * <TextButton disabled>Unavailable</TextButton>
 *
 * @example Icon-only (requires aria-label)
 * <TextButton aria-label="Close" leadingIcon={<CloseIcon />} />
 */
